import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button } from '../Button/Button'

/**
 * Bronson MediaTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-media-teaser.html
 *
 * @param {string} [buttonLabel] - The content for the MediaTeaser button.
 * @param {string} [buttonModifier] - Additional button classes.
 * @param {object} [buttonProps] - Additional button props.
 * @param {React.ReactNode|React.ReactNode[]} [children] - Content for the MediaTeaser body section.
 * @param {string} [className] - Additional classes to append to the MediaTeaser element.
 * @param {boolean} [fullLink] - Render the `fullLink` variant.
 * @param {React.ReactNode} [fullLinkFooter] - Content for the footer if the {@link fullLink} prop is enabled.
 * @param {{ sizes: string, src: string, alt: string, srcSet: string }} [imgProps] - An object of image properties.
 * @param {boolean} [linkButton] - Render the `linkButton` variant.
 * @param {function} [onButtonClick] - Callback function to MediaTeaser button.
 * @param {string} [pretitle] - Content for the pretitle element.
 * @param {boolean} [reversed] - Reverse the order of the image and body section.
 * @param {string} [title] - Content for the title heading.
 * @param {boolean} [vertical] - Render the `vertical` variant to displays the image section on top of the body section.
 * @param {React.ReactNode} [video] - Replace the MediaTeaser image with a {@link Video} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The MediaTeaser component.
 * @constructor
 */
export function MediaTeaser({
  buttonLabel,
  buttonModifier,
  buttonProps,
  children,
  className,
  fullLink,
  fullLinkFooter,
  imgProps,
  linkButton,
  onButtonClick,
  pretitle,
  reversed,
  title,
  vertical,
  video,
  testId,
  ...otherProps
}) {
  const sectionClassNameList = classNames(
    'c-media-teaser',
    {
      // Main class modifier convenience prop extension.
      'c-media-teaser--reversed': reversed, // Convenience prop from Bronson variants.
      'c-media-teaser--vertical': vertical, // Convenience prop from Bronson variants.
      'c-media-teaser--full-link': fullLink, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  const btnClassNameList = classNames(
    {
      'c-media-teaser__link': linkButton && !fullLink,
    },
    buttonModifier
  ).trim()

  const renderIfFullLink = () => {
    if (fullLink) {
      return fullLinkFooter
    }
    return null
  }

  function renderIfButtonLabel() {
    if (buttonLabel) {
      return linkButton ? (
        <>
          <Button
            className={btnClassNameList}
            onClick={onButtonClick}
            element="a"
            icon="semantic-forward"
            link
            small
            iconReversed
            {...buttonProps}
          >
            {buttonLabel}
          </Button>
        </>
      ) : (
        <>
          <Button className={btnClassNameList} onClick={onButtonClick} {...buttonProps}>
            {buttonLabel}
          </Button>
        </>
      )
    }
    return null
  }

  return (
    <section {...otherProps} data-testid={testId} className={sectionClassNameList}>
      <div className="c-media-teaser__media">
        {video || (imgProps && <img className="c-media-teaser__image" alt="" {...imgProps} />)}
      </div>
      <div className="c-media-teaser__caption">
        <div className="c-media-teaser__pretitle">{pretitle}</div>
        <h3 className="c-media-teaser__title">{title}</h3>
        <div className="c-media-teaser__body">{children}</div>
        <div className="c-media-teaser__footer">
          {renderIfFullLink()}
          {renderIfButtonLabel()}
        </div>
      </div>
    </section>
  )
}

MediaTeaser.propTypes = {
  buttonLabel: PropTypes.string, // Bronson template: 'media-teaser-button-text'.
  buttonModifier: PropTypes.string, // Bronson template: 'media-teaser-button-modifier'.
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]), // Bronson template: 'media-teaser-text'.
  className: PropTypes.string, // Bronson template: 'media-teaser-modifier'.
  imgProps: PropTypes.shape({
    alt: PropTypes.string,
    srcSet: PropTypes.string,
    src: PropTypes.string,
    sizes: PropTypes.string,
  }), // Bronson template: 'srcset'.
  linkButton: PropTypes.bool, // Bronson template: 'media-teaser-has-link-button'.
  onButtonClick: PropTypes.func, // Bronson template: 'media-teaser-button-link-href'.
  pretitle: PropTypes.string, // Bronson template: 'media-teaser-pretitle'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  title: PropTypes.string, // Bronson template: 'media-teaser-title'.
  video: PropTypes.node, // Bronson template: '@bronson-video--video-youtube'.
  /* Convenience props. */
  fullLink: PropTypes.bool, // Convenience prop for `.c-media-teaser--full-link`. Bronson template: 'media-teaser-modifier'.
  fullLinkFooter: PropTypes.node, // See Bronson MediaTeaser `fullLink`.
  reversed: PropTypes.bool, // Convenience prop for `.c-media-teaser--reversed`. Bronson template: 'media-teaser-modifier'.
  vertical: PropTypes.bool, // Convenience prop for `.c-media-teaser--vertical`. Bronson template: 'media-teaser-modifier'.
}
